import { createApi } from '@reduxjs/toolkit/query/react';
import customBaseQuery from '@/lib/customBaseQuery';

const patientsBaseUrl = '/api/patients';
export const patientsApi = createApi({
  reducerPath: 'patientsApi',
  baseQuery: customBaseQuery,
  tagTypes: ['PatientRequest', 'PatientTransfer'],
  endpoints: (builder) => ({
    createPatientRequest: builder.mutation<PatientReqResponse, CreatePatientRequestPayload>({
      query: (body) => ({
        url: `${patientsBaseUrl}/request`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PatientRequest'],
    }),
    createPatientTransfer: builder.mutation<PatientsData[], CreatePatientTransferPayload>({
      query: ({ accessToken, ...body }) => ({
        url: `${patientsBaseUrl}/transfer`,
        method: 'POST',
        params: accessToken ? { access_token: accessToken } : undefined,
        body,
      }),
      invalidatesTags: ['PatientTransfer'],
    }),
    createBulkPatientTransfer: builder.mutation<
      PatientBulkCreateResponse,
      CreateBulkPatientTransferPayload
    >({
      query: ({ accessToken, ...body }) => ({
        url: `${patientsBaseUrl}/bulk-transfer`,
        method: 'POST',
        params: accessToken ? { access_token: accessToken } : undefined,
        body,
      }),
      invalidatesTags: ['PatientTransfer'],
    }),
    getPatientRequestById: builder.query<
      PatientReqGetOneResponse,
      { id: number; accessToken?: string | null }
    >({
      query: ({ id, accessToken }) => ({
        url: `${patientsBaseUrl}/request/${id}`,
        params: accessToken ? { access_token: accessToken } : undefined,
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'PatientRequest', id }],
    }),
    getPatientTransfersByRequestId: builder.query<
      PatientTransferRequestResp[],
      { id: number; accessToken?: string | null }
    >({
      query: ({ id, accessToken }) => ({
        url: `${patientsBaseUrl}/transfer-request/${id}`,
        params: accessToken ? { access_token: accessToken } : undefined,
      }),
      providesTags: (_result, _error, { id }) => [
        { type: 'PatientTransfer', id: `REQUEST-${id}` },
        'PatientTransfer',
      ],
    }),
    getPatientTransfersByRequestIdAndFacility: builder.query<
      PatientTransferRequestResp[],
      { id: number; facilityId: number; accessToken?: string | null }
    >({
      query: ({ id, facilityId, accessToken }) => ({
        url: `${patientsBaseUrl}/transfer-request/${id}/facility/${facilityId}`,
        params: accessToken ? { access_token: accessToken } : undefined,
      }),
      providesTags: (_result, _error, { id, facilityId }) => [
        { type: 'PatientTransfer', id: `REQUEST-${id}-FACILITY-${facilityId}` },
        'PatientTransfer',
      ],
    }),
    getPatientTransfersByFacilityId: builder.query<PatientTransferRequestResp[], number>({
      query: (id) => ({
        url: `${patientsBaseUrl}/transfer-facility/${id}`,
      }),
      providesTags: (_result, _error, id) => [{ type: 'PatientTransfer', id: `FACILITY-${id}` }, 'PatientTransfer'],
    }),
    updateReceivingPatientTransferStatus: builder.mutation<
      PatientsData[],
      UpdateReceivingPatientTransferPayload
    >({
      query: ({ accessToken, ...body }) => ({
        url: `${patientsBaseUrl}/receiving-transfer`,
        method: 'post',
        params: accessToken ? { access_token: accessToken } : undefined,
        body,
      }),
      invalidatesTags: ['PatientTransfer'],
    }),
  }),
});

export const {
  useCreateBulkPatientTransferMutation,
  useCreatePatientRequestMutation,
  useCreatePatientTransferMutation,
  useGetPatientRequestByIdQuery,
  useGetPatientTransfersByRequestIdQuery,
  useGetPatientTransfersByRequestIdAndFacilityQuery,
  useGetPatientTransfersByFacilityIdQuery,
  useLazyGetPatientTransfersByRequestIdQuery,
  useUpdateReceivingPatientTransferStatusMutation,
} = patientsApi;
